import { useState } from 'react'
import { useListAssociatedParties } from '@/shared/hooks/api/associatedParties'
import type { AssociatedParty } from '@/shared/hooks/api/associatedParties'
import { Plus, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { AddAssociatedPartyModal } from './AddAssociatedPartyModal'
import { formatAssociatedPartyAddress } from './formatAddress'

export function AssociatedPartiesPage() {
  const { t } = useTranslation(['associatedParties', 'countries', 'common'])
  const { data: parties = [], isLoading } = useListAssociatedParties()
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const navigate = useNavigate()

  const openDetails = (partyId: string) => {
    navigate(`/associated-parties/${partyId}`)
  }

  if (isLoading) {
    return (
      <>
        <Header fixed>
          <Search />
          <div className='ms-auto flex items-center space-x-4'>
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
          <div className='flex items-center justify-center py-12 text-muted-foreground'>
            {t('loading')}
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header fixed>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
        <div className='flex flex-wrap items-end justify-between gap-2'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>{t('title')}</h2>
            <p className='text-muted-foreground'>{t('description')}</p>
          </div>
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus className='size-4' />
            {t('addParty')}
          </Button>
        </div>

        {parties.length === 0 ? (
          <div className='flex flex-1 flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16'>
            <Users className='size-10 text-muted-foreground/50' />
            <div className='space-y-1 text-center'>
              <h3 className='text-lg font-semibold'>{t('emptyState.title')}</h3>
              <p className='text-sm text-muted-foreground'>
                {t('emptyState.description')}
              </p>
            </div>
            <Button onClick={() => setIsAddModalOpen(true)}>
              <Plus className='size-4' />
              {t('emptyState.action')}
            </Button>
          </div>
        ) : (
          <div className='overflow-x-auto rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('list.columnName')}</TableHead>
                  <TableHead>{t('list.columnAddress')}</TableHead>
                  <TableHead>{t('list.columnContactName')}</TableHead>
                  <TableHead>{t('list.columnContactPhoneNumber')}</TableHead>
                  <TableHead>{t('list.columnContactEmail')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {parties.map((party: AssociatedParty) => (
                  <TableRow
                    key={party.id}
                    className='cursor-pointer'
                    onClick={() => openDetails(party.id)}
                  >
                    <TableCell>{party.name}</TableCell>
                    <TableCell
                      className='max-w-xs truncate'
                      title={formatAssociatedPartyAddress(party, {
                        getCountryName: (code) =>
                          t(code, { ns: 'countries' }) || code,
                        translate: (key, options) => t(key, options),
                      })}
                    >
                      {formatAssociatedPartyAddress(party, {
                        getCountryName: (code) =>
                          t(code, { ns: 'countries' }) || code,
                        translate: (key, options) => t(key, options),
                      })}
                    </TableCell>
                    <TableCell>
                      {party.contactName ?? t('emptyValue')}
                    </TableCell>
                    <TableCell>
                      {party.contactPhoneNumber ?? t('emptyValue')}
                    </TableCell>
                    <TableCell>
                      {party.contactEmail ?? t('emptyValue')}
                    </TableCell>
                    <TableCell
                      className='text-right'
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => openDetails(party.id)}
                      >
                        {t('common:actions.view')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Main>

      <AddAssociatedPartyModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
      />
    </>
  )
}
